import React, { useEffect, useState } from 'react';
import { useGame } from 'context/GameContext';
import clock from 'util/clock';
import * as Styled from './ClockDebug.style';

// The clock only publishes gameState on a phase boundary, so the readout polls for the
// in-between values (the accumulator) on its own schedule.
const POLL_MS = 100;

const round = (ms, places = 1) => (ms / 1000).toFixed(places);

// Which clause of canTick is holding time, in the order the predicate evaluates them.
const heldBy = readout => {
  const { enabled, paused, canTick, gates } = readout;
  if (!enabled) return 'interval disabled';
  if (paused) return 'paused';
  if (canTick) return null;
  if (!gates.hasMap) return 'map not generated';
  // The clock runs on the walk and in a town. Everywhere else — the open road — is untimed,
  // and the phase name is not the reason, so say where the player actually is.
  if (!gates.inWalkPhase && !gates.inTown) return 'on the road, off the clock';
  if (gates.currentEvent) return `event: ${gates.currentEvent}`;
  if (gates.queued) return `${gates.queued} event(s) queued`;
  return 'unknown';
};

const stateOf = readout => {
  if (!readout.enabled) return { label: 'off', tone: 'bad' };
  if (readout.paused) return { label: 'paused', tone: 'warn' };
  if (!readout.canTick) return { label: 'frozen', tone: 'warn' };
  return { label: 'running', tone: 'ok' };
};

/**
 * Dev-only clock readout. Gated on Clock().showDebug — on in development, off in a production
 * build, and off in .env.test so its text cannot collide with the e2e locators.
 *
 * It exists because canTick is a compound predicate: when time stops the useful question is
 * *which* clause stopped it, and that is invisible from the game screen.
 */
const ClockDebug = () => {
  const { gameState } = useGame();
  const [readout, setReadout] = useState(clock.inspect());
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const id = setInterval(() => setReadout(clock.inspect()), POLL_MS);
    return () => clearInterval(id);
  }, []);

  // Null until useGameUpdate's effect has attached its controls.
  if (!readout) return null;

  const { party, calendar } = gameState;
  const state = stateOf(readout);
  const held = heldBy(readout);
  const remaining = Math.max(0, readout.phaseMs - readout.accumulator);

  return (
    <Styled.Panel>
      <Styled.Title onClick={() => setOpen(prev => !prev)}>
        <span>clock {open ? '▾' : '▸'}</span>
        <Styled.State tone={state.tone}>{state.label}</Styled.State>
      </Styled.Title>

      {open && (
        <>
          <Styled.Row>
            <span>next phase</span>
            <span>
              {round(remaining)}s / {round(readout.phaseMs, 0)}s
            </span>
          </Styled.Row>
          <Styled.Bar fraction={readout.accumulator / readout.phaseMs}>
            <div />
          </Styled.Bar>

          <Styled.Row>
            <span>time of day</span>
            <span>{calendar.isNight() ? 'night' : 'day'}</span>
          </Styled.Row>
          <Styled.Row>
            <span>day counter</span>
            <span>
              {calendar.step}
              {readout.gates.countsDays ? '' : ' (held)'}
            </span>
          </Styled.Row>
          <Styled.Row>
            <span>party phase</span>
            <span>{party.phase?.name}</span>
          </Styled.Row>
          <Styled.Row>
            <span>place</span>
            <span>{readout.gates.city || (readout.gates.inWalkPhase ? 'the walk' : 'the road')}</span>
          </Styled.Row>
          <Styled.Row>
            <span>hp</span>
            <span>{party.resources.attribs.health.value}</span>
          </Styled.Row>
          <Styled.Row>
            <span>phases to town</span>
            <span>{party.phasesToTown}</span>
          </Styled.Row>
          <Styled.Row>
            <span>accumulator</span>
            <span>
              {readout.accumulator}ms / {readout.budget}ms
            </span>
          </Styled.Row>

          {held && (
            <Styled.Row>
              <span>held by</span>
              <Styled.State tone="warn">{held}</Styled.State>
            </Styled.Row>
          )}

          <Styled.Buttons>
            <button onClick={() => clock.toggle()}>{readout.paused ? 'resume' : 'pause'}</button>
            <button onClick={() => clock.advance(1)}>+phase</button>
            <button onClick={() => clock.reset()}>reset</button>
          </Styled.Buttons>
        </>
      )}
    </Styled.Panel>
  );
};

export default ClockDebug;
